// This code creates an Axios instance with withCredentials set to true,
// which is necessary for including CSRF tokens in requests.
// It sets a base URL for all requests.
// An interceptor is added to inject the CSRF token from cookies into the request headers for every outgoing request.
// This ensures CSRF protection for your API requests.

import axios, { AxiosInstance } from 'axios';
import { getJwtToken } from './apiServiceCSRF';
import { Capacitor } from '@capacitor/core';
import { isMobile, isDesktop, isTablet } from './admin_actions/apiUtils';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

const addJwtToken = async (config: any) => {
  if (!Capacitor.isNativePlatform()) return;
  const jwtToken = await getJwtToken();
  if (jwtToken && config.headers) {
    config.headers['Authorization'] = `Bearer ${jwtToken}`;
  }
};

const addCsrfToken = (config: any) => {
  const csrfTokenCookie = document.cookie.split('; ').find((row) => row.startsWith('XSRF-TOKEN='));
  if (csrfTokenCookie && config.headers) {
    const csrfToken = decodeURIComponent(csrfTokenCookie.split('=')[1]);
    config.headers['X-XSRF-TOKEN'] = csrfToken;
  }
};

const addDeviceTypeHeaders = async (config: any) => {
  const isNative = Capacitor.isNativePlatform();
  if (config.headers) {
    config.headers['X-Device-Type'] = isNative ? 'native' : 'web';
    config.headers['X-Platform'] = isNative ? Capacitor.getPlatform() : 'web';
    config.headers['X-App-Version'] = isNative ? await CapacitorUpdater.current().then(x => x.bundle.version) : 'web';
    config.headers['X-Screen-Type'] = isMobile.value ? 'mobile' : isTablet.value ? 'tablet' : 'desktop';
  }
};

// Set up for axiosClassic instance
const defaultUseClassicProxy = import.meta.env.VITE_USE_CLASSIC_PROXY !== 'false';

// Builds the classic content base URL. Pass `useProxy` to override the env default
// on a per-request basis (e.g. a route that opts a page in/out of the PHP proxy).
export const getClassicBaseURL = (useProxy: boolean = defaultUseClassicProxy): string => {
  const classicPath = useProxy ? 'classic' : 'classicPage';
  return `${import.meta.env.VITE_LEVEL_TECH_API_URL}/api/${classicPath}/`;
};

const axiosClassic: AxiosInstance = axios.create({
  withCredentials: true,
  baseURL: getClassicBaseURL(),
});

axiosClassic.interceptors.request.use(
  async (config) => {
    addCsrfToken(config);
    await addJwtToken(config);
    await addDeviceTypeHeaders(config);
    config.url = config.url!.replace('.php', '');
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosClassic.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Set up for axiosLevelTech instance
const baseURL = import.meta.env.VITE_LEVEL_TECH_API_URL;
const axiosLevelTech: AxiosInstance = axios.create({
  withCredentials: true,
  baseURL: baseURL,
});

axiosLevelTech.interceptors.request.use(
  async (config) => {
    addCsrfToken(config);
    await addJwtToken(config);
    await addDeviceTypeHeaders(config);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosLevelTech.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);
 
export { axiosClassic, axiosLevelTech };
